import { useQueryClient } from "@tanstack/react-query";
import { Breadcrumb, Form, message } from "antd";
import { FormInstance } from "antd/lib/form";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { CustomButton, CustomInput } from "../../components";
import localStorageKey from "../../enumerations/localStorageKey";
import queryKey from "../../enumerations/queryKey";
import {
  useForgotPassword,
  useSendForgotPasswordMail,
} from "../../hooks/SignIn/signin.hooks";
import {
  usePostEmailSend,
  useVerifyEmail,
} from "../../hooks/SignUp/signup.hooks";
import styles from "./style.module.scss";

const Verification = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const cache = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const { mutate: verifyEmail } = useVerifyEmail();
  const { mutate: resendCode } = usePostEmailSend();
  const { mutate: forgetPassword } = useForgotPassword();
  const { mutate: resendPasswordCode } = useSendForgotPasswordMail();
  const location = useLocation();
  const _state = location?.state;
  const [hasPassword, _sethasPassword] = useState(_state?.hasPassword || false);
  const userEmail = window.localStorage.getItem(localStorageKey.USER_ACCOUNT);
  const localCart = window.localStorage.getItem(localStorageKey.CART);
  const [counter, setCounter] = useState<number>(90);
  const [isResendDisabled, setIsResendDisabled] = useState<boolean>(true);
  const [isLoadingResend, setIsLoadingResend] = useState<boolean>(false);
  const [isLoadingConfirm, setIsLoadingConfirm] = useState<boolean>(false);
  const [isLoadingPassword, setIsLoadingPassword] = useState<boolean>(false);
  const [code, setCode] = useState({ code: "" });
  const [values, setValues] = useState({
    account: "",
    new_password: "",
    confirm_new_password: "",
    code: "",
  });

  const handleResendCode = async () => {
    setIsLoadingResend(true);
    await resendCode(undefined, {
      onSuccess: () => {
        setCounter(90);
        setIsResendDisabled(true);
        window.localStorage.setItem(
          localStorageKey.EMAIL_VERIFY_SEND_TIME,
          JSON.stringify({
            time: new Date().valueOf(),
          })
        );
        setIsLoadingResend(false);
      },
      onError: (error: any) => {
        console.debug(error);
        setIsLoadingResend(false);
      },
    });
  };

  const handleResendPasswordCode = async () => {
    await resendPasswordCode({ params: { account: _state.account.account } });
    setCounter(90);
    setIsResendDisabled(true);
    window.localStorage.setItem(
      localStorageKey.EMAIL_VERIFY_SEND_TIME,
      JSON.stringify({
        time: new Date().valueOf(),
      })
    );
  };

  const handleSubmit = async () => {
    setIsLoadingConfirm(true);
    await verifyEmail(
      {
        params: code,
      },
      {
        onSuccess: (res: any) => {
          cache.invalidateQueries({ queryKey: [queryKey.USER_INFO] });
          setTimeout(() => {
            process.env.REACT_APP_PROD_NAME_DEV === "2esim"
              ? localCart
                ? navigate("/cart")
                : navigate("/")
              : navigate("/usercenter");
          }, 1000);
          setIsLoadingConfirm(false);
        },
        onError: (error: any) => {
          messageApi.open({
            type: "error",
            content: error,
          });
          setIsLoadingConfirm(false);
        },
      }
    );
  };
  const handleForgetPassword = async () => {
    setIsLoadingPassword(true);
    await forgetPassword(
      {
        params: { ...values, account: _state.account.account },
      },
      {
        onSuccess: (res: any) => {
          navigate("/success");
          setIsLoadingPassword(false);
        },
        onError: (error: any) => {
          messageApi.open({
            type: "error",
            content: error,
          });
          setIsLoadingPassword(false);
        },
      }
    );
  };

  const handleChange = (prop: any) => (event: any) => {
    setCode({ ...code, [prop]: event.target.value });
  };
  const handlePasswordChange = (prop: any) => (event: any) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  useEffect(() => {
    window.localStorage.setItem(
      localStorageKey.EMAIL_VERIFY_SEND_TIME,
      JSON.stringify({
        time: new Date().valueOf(),
      })
    );
    const savedTime = JSON.parse(
      window.localStorage.getItem(localStorageKey.EMAIL_VERIFY_SEND_TIME) || "0"
    );
    const currentTime = new Date().valueOf();
    const diffInSeconds = Math.floor((currentTime - savedTime.time) / 1000);
    const remainingTime = Math.max(0, 90 - diffInSeconds);
    setCounter(remainingTime);
    setIsResendDisabled(remainingTime > 0);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (counter > 0 && isResendDisabled) {
      timer = setTimeout(() => {
        setCounter((prevCounter) => prevCounter - 1);
      }, 1000);
    } else if (counter === 0 && isResendDisabled) {
      setIsResendDisabled(false);
    }
    return () => clearTimeout(timer);
  }, [counter, isResendDisabled]);

  return (
    <>
      {contextHolder}
      <div className={styles.breadCrumb}>
        {hasPassword ? (
          <Breadcrumb
            separator=">"
            items={[
              {
                title: t("normal.forgotPassword"),
              },
              {
                title: t("forgotPassword.enterEmail.page"),
              },
              {
                title: t("verify.header"),
              },
            ]}
          />
        ) : (
          <Breadcrumb
            separator=">"
            items={[
              {
                title: t("signup.quickSignup"),
              },
              {
                title: t("verify.header"),
              },
            ]}
          />
        )}
      </div>
      <div className={styles.verifyContainer}>
        <div className={styles.verifyTitle}>{t("verify.header")}</div>
        <div className={styles.verifyWrapper}>
          <div className={styles.verifyDesc}>
            {t("verify.codeSent.msg", {
              email: userEmail ? userEmail : _state?.account.account,
            })}
          </div>
          <Form
            form={form}
            name="verification"
            autoComplete="off"
            requiredMark={false}
            layout="vertical"
            onFinish={hasPassword ? handleForgetPassword : handleSubmit}
          >
            {hasPassword ? (
              <>
                <div className={styles.codeGroup}>
                  <CustomInput
                    formItemProps={{
                      name: "code",
                      label: t("verify.codeLabel"),
                      rules: [
                        {
                          required: true,
                          message: t("form.required", {
                            name: t("normal.verifyCode"),
                          }),
                        },
                      ],
                    }}
                    inputProps={{
                      placeholder: t("signup.enterInput", { content: "" }),
                      onChange: handlePasswordChange("code"),
                    }}
                  />
                  <CustomButton
                    type="primary"
                    size="middle"
                    disabled={isResendDisabled}
                    htmlType="submit"
                    style={{
                      width: "auto",
                      flexBasis: "40%",
                    }}
                    onClick={handleResendPasswordCode}
                  >
                    {isResendDisabled
                      ? t("verify.resendCode") + `(${counter}s)`
                      : t("verify.resendCode")}
                  </CustomButton>
                </div>
                <CustomInput
                  formItemProps={{
                    name: "new_password",
                    label: t("forgotPassword.enterNewPassword"),
                    rules: [
                      {
                        required: true,
                        message: t("form.required", {
                          name: t("normal.password"),
                        }),
                      },
                      {
                        min: 6,
                        max: 18,
                        pattern: /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{6,18}$/,
                        message: t("normal.password.validation.error"),
                      },
                    ],
                  }}
                  formItemType="password"
                  inputProps={{
                    disabled: isLoadingPassword,
                    placeholder: t("signup.enterInput", { content: "" }),
                    onChange: (e) => {
                      handlePasswordChange("new_password")(e);
                      form.validateFields(["confirm_new_password"]);
                    },
                  }}
                />
                <CustomInput
                  formItemProps={{
                    name: "confirm_new_password",
                    label: t("forgotPassword.enterNewPasswordAgain"),
                    rules: [
                      {
                        required: true,
                        message: t("form.required", {
                          name: t("signup.confirmPassword"),
                        }),
                      },
                      ({
                        getFieldValue,
                      }: {
                        getFieldValue: FormInstance["getFieldValue"];
                      }) => ({
                        validator(_: any, value: any) {
                          if (
                            !value ||
                            getFieldValue("new_password") === value
                          ) {
                            return Promise.resolve();
                          }

                          return Promise.reject(
                            new Error(t("signup.confirmPasswordError"))
                          );
                        },
                      }),
                    ],
                  }}
                  formItemType="password"
                  inputProps={{
                    placeholder: t("signup.enterInput", { content: "" }),
                    onChange: handlePasswordChange("confirm_new_password"),
                  }}
                />
              </>
            ) : (
              <div className={styles.codeGroup}>
                <CustomInput
                  formItemProps={{
                    name: "code",
                    label: t("verify.codeLabel"),
                    rules: [
                      {
                        required: true,
                        message: t("form.required", {
                          name: t("normal.verifyCode"),
                        }),
                      },
                    ],
                  }}
                  inputProps={{
                    placeholder: t("signup.enterInput", { content: "" }),
                    onChange: handleChange("code"),
                  }}
                />
                <CustomButton
                  type="primary"
                  size="middle"
                  disabled={isResendDisabled || isLoadingResend}
                  style={{
                    width: "auto",
                    padding: "16px",
                  }}
                  loading={isLoadingResend}
                  onClick={handleResendCode}
                >
                  {isResendDisabled
                    ? t("verify.resendCode") + `(${counter}s)`
                    : t("verify.resendCode")}
                </CustomButton>
              </div>
            )}
            {/* <Spin spinning={isLoadingConfirm}> */}
            <CustomButton
              type="primary"
              size="large"
              htmlType="submit"
              style={{
                width: "100%",
              }}
              disabled={isLoadingConfirm}
              loading={isLoadingConfirm}
            >
              {t("normal.confirm")}
            </CustomButton>
            {/* </Spin> */}
          </Form>
        </div>
      </div>
    </>
  );
};

export default Verification;
